import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link, withRouter } from 'react-router';
import { connectLean } from 'lean-redux';
import { connect } from 'react-redux';
import cn from 'classnames';
import { Icon } from 'legacy-packages/ft-cb';
import get from 'lodash/get';
import log from 'chameleon/ui-stack/utilities/log';
import {
  getResourceAddress,
  getSignedUnitResource,
  getValidBucketUrl,
  isS3Resource,
} from 'legacy/src/utility/utils';
import {
  timeLeftUntilCbSessionTimeout,
  timeLeftUntilCBSessionExpire,
} from 'legacy/src/components/auth/utils';
import ShareableResourcePopover from 'legacy/packages/unit-outline/src/components/_shared/ShareableResourcePopover';
import ResourceTypes, {
  ResourceContentTypes,
} from 'legacy/packages/unit-outline/src/constants';
import trackExternalResource from '../../../utils/trackExternalResource';
import authenticatedPath from '../../../utils/authenticatedPath';
import { getTopicQuestionsFilter } from '../util';
import {
  timedAssignmentNotification,
  sessionExpirationNotification,
} from 'legacy/src/utility/helpers';
import shouldShowAssessmentTab from 'legacy-src/utility/helpers/shouldShowAssessmentTab';
import ResourceWithDescription from 'legacy/packages/unit-outline/src/components/_shared/CourseResource/ResourceWithDescription';

import './styles.scss';

class CourseResource extends Component {
  static displayName = 'CourseResource';

  static propTypes = {
    resource: PropTypes.object.isRequired,
    children: PropTypes.any,
    unitNumber: PropTypes.string,
    subunitNumber: PropTypes.string,
    isStudent: PropTypes.bool,
    onClick: PropTypes.func,
    widgetTagColumn: PropTypes.string,
    itemAction: PropTypes.any,
    isUnlocked: PropTypes.bool,
    skipCustomFilters: PropTypes.bool,
    assignmentOverview: PropTypes.object,
    showFileSize: PropTypes.bool,
    showSharedIcon: PropTypes.bool,
    className: PropTypes.string,
    baseQuery: PropTypes.object,
    unitTitle: PropTypes.string,
    unitsLabel: PropTypes.string,
    showDescription: PropTypes.bool,
    showIcon: PropTypes.bool,
    customDisplayName: PropTypes.string,
  };

  static defaultProps = {
    children: undefined,
    unitNumber: undefined,
    subunitNumber: undefined,
    isStudent: false,
    itemAction: null,
    isUnlocked: false,
    skipCustomFilters: false,
    assignmentOverview: {},
    showFileSize: true,
    showSharedIcon: true,
    className: '',
    baseQuery: {},
    unitTitle: '',
    unitsLabel: null,
    showDescription: true,
    customDisplayName: '',
    showIcon: true,
  };

  constructor(props) {
    super(props);

    this.onClick = (...args) => {
      props.onClick && props.onClick();
      trackExternalResource.apply(this, args);
    };

    const {
      params,
      resource,
      unitNumber,
      subunitNumber,
      location,
      metadata,
      isStudent,
      baseQuery,
      useClassroomResourceAddress,
    } = props;
    const { subject } = params || {};
    const resourceAttributes = this.getLinkAttributes({
      subject,
      resource,
      unitNumber,
      subunitNumber,
      location,
      metadata,
      isStudent,
      baseQuery,
    });

    if (
      resource.__typename === ResourceTypes.URL &&
      useClassroomResourceAddress &&
      isS3Resource(resourceAttributes.url)
    ) {
      resourceAttributes.url = getResourceAddress(
        resourceAttributes.url,
        subject,
      );
    }

    this.state = {
      resourceAttributes,
    };

    this.linkRef = React.createRef();
  }

  componentDidMount() {
    const {
      resourceAttributes: { url },
    } = this.state;
    const {
      resource,
      useClassroomResourceAddress,
      location: { state } = {},
    } = this.props;

    if (state) {
      const isFocusedAssessment =
        state.focusedElement === resource.assessmentId;
      const isFocusedVideo =
        state.focusedElementVideo ===
        `${this.props.resource.url}_${this.props.resource.videoId}`;
      if (isFocusedAssessment || isFocusedVideo) {
        setTimeout(() => {
          const focus = get(this, 'linkRef.current.focus');
          if (focus && typeof focus === 'function') {
            focus();
          }
        });
      }
    }

    if (!useClassroomResourceAddress) {
      const urlBucket = (url && getValidBucketUrl(url)) || null;
      if (
        resource.__typename === ResourceTypes.URL &&
        url.includes(urlBucket)
      ) {
        getSignedUnitResource(url).then((signedResourceURL) => {
          let resourceAttributes = { ...this.state.resourceAttributes };
          resourceAttributes.url = signedResourceURL;
          this.setState({
            resourceAttributes,
          });
        });
      }
    }
  }

  /**
   * @todo - refactor and remove unused query params from pathname (url)
   * */
  getLinkAttributes = (args) => {
    const {
      subject,
      resource,
      unitNumber,
      subunitNumber,
      location,
      metadata,
      baseQuery,
    } = args;
    let url;
    let icon;
    let external;
    let returnPath;
    let quizId;
    let resourceQuery;
    const { url: resourceUrl } = resource;

    const { isTeacher } = this.props;
    const { getTabUrl } = shouldShowAssessmentTab({ isTeacher });

    switch (resource.__typename) {
      // Groups should never be visible here because of the filter, but just in case, this will keep it from crashing
      // the application.
      case ResourceTypes.GROUP:
        url = '';
        icon = null;
        external = false;
        break;
      case ResourceTypes.URL:
        url = resourceUrl;
        icon =
          resource.contentType === ResourceContentTypes.EXTERNAL_LINK
            ? 'external_link'
            : 'download';
        external = true;
        break;
      case ResourceTypes.PARTNER_LINK:
      case ResourceTypes.DISTANCE_LINK:
        url = resourceUrl || '';
        icon = 'arrow-east';
        external = true;
        break;
      case ResourceTypes.TEACHER_MODULE: {
        url = authenticatedPath(`${CB_LEGACY_URL}${resource.coursePath}`);
        icon = 'teacher_module';
        external = true;
        break;
      }
      case ResourceTypes.ASSESSMENT: {
        const { pathname, search } = location;
        const expandPath = `/expand-unit/${unitNumber}${
          subunitNumber ? `/subunit/${subunitNumber}` : ''
        }`;
        if (pathname.includes('/units')) {
          returnPath = `/${subject}/units${expandPath}${search}`;
        } else {
          returnPath = `/${subject}/home${search}`;
        }
        if (unitNumber) {
          if (subunitNumber) {
            // unit test, v1 fallback
            url = `/${subject}/assessments/assign/${unitNumber}/unit/${unitNumber}.${subunitNumber}/subunit?returnPath=${returnPath}`;
          } else {
            // focus quiz, v1 fallback
            url = `/${subject}/assessments/assign/${unitNumber}/unit?returnPath=${returnPath}`;
          }
        }
        // v2, supports multiple assessments.
        if (resource.assessmentId) {
          quizId = resource.assessmentId;
          url = `/${subject}/assessments/${getTabUrl('assign')}`;
        }
        icon = 'multi_choice_border';
        break;
      }
      case ResourceTypes.QUESTION_BANK:
        url = `/${subject}/question_bank/create/${unitNumber}`;
        if (Number.parseInt(subject, 10) === 103) {
          url += `/big_idea/only`;
        } else {
          url += `/unit`;
        }

        icon = 'practice-question';
        break;

      case ResourceTypes.PRACTICE_QUESTIONS: {
        url = `/${subject}/question_bank/create/filters`;
        icon = 'lightbulb';
        break;
      }

      case ResourceTypes.EBOOK: {
        url = resourceUrl;
        icon = 'ebook';
        break;
      }

      case ResourceTypes.YOUTUBE: {
        url = resourceUrl;
        icon = 'youtube';
        external = true;
        break;
      }

      case ResourceTypes.PERFORMANCE_TASK:
        url = `/${subject}/question_bank/create/${unitNumber}/big_idea`;
        icon = 'practice-question';
        break;

      case ResourceTypes.EMBEDDED_VIDEO:
        // TODO use the videoId
        url = `/${subject}/home`;
        resourceQuery = {
          ...baseQuery,
          apd: resourceUrl,
        };
        icon = 'ap_daily';
        break;

      default:
        log('warn', `Unrecognized resource type: ${resource.__typename}`);
    }

    if (resource.icon) {
      icon = resource.icon;
    }

    return { url, icon, external, returnPath, metadata, quizId, resourceQuery };
  };

  handleClickTimedAssignment = ({
    urlNoReturnPath,
    router,
    timer,
    checkCBSessionFlag,
    showTimedAssignmentWarning,
    returnPath,
  }) => {
    const useCreateTimeOrExpireTime = this.props.levityUseCreateTime
      ? timeLeftUntilCbSessionTimeout()
      : timeLeftUntilCBSessionExpire();
    // hard override only used for debugging, should remain off, see FY-12447
    const expirationTimeDiff =
      this.props.cbSessionExpirationOverride || useCreateTimeOrExpireTime;
    const expiringSession = expirationTimeDiff <= timer;
    if (checkCBSessionFlag && showTimedAssignmentWarning && expiringSession) {
      sessionExpirationNotification(expirationTimeDiff, timer, () => {
        router.push(urlNoReturnPath);
      });
    } else if (checkCBSessionFlag && showTimedAssignmentWarning) {
      timedAssignmentNotification(timer, () => {
        router.push(urlNoReturnPath);
      });
    } else {
      router.push({
        pathname: urlNoReturnPath,
        state: { returnPath, focusedElement: this.props.resource.assessmentId },
      });
    }
  };

  renderResourceLink = ({
    isLinkDisabled,
    displayName,
    external,
    externalLinkOnClick,
    urlNoReturnPath,
    returnPath,
    unitNumber,
    topicQuestionsFilter,
    router,
    timer,
    metadata,
    url,
    checkCBSessionFlag,
    showTimedAssignmentWarning,
    quizId,
    isAdminOrCoordinator,
    isEmbeddedResource,
    resourceQuery,
    embedVideoTitle,
    hasUnlockedIcon,
    resourceTypeName,
    isDistanceResource,
    isPartnerResource,
    isURLResource,
    isGoogleClassroomShareable,
    customDisplayName,
  }) => {
    const displayNameSpan = (
      <span
        className={cn('resource-label', { disabled: isLinkDisabled })}
        key="label"
      >
        {customDisplayName || displayName}
      </span>
    );
    const className = isURLResource
      ? 'resource-title-link'
      : 'course-resource row-align-top';
    let query = { returnPath, quizId };
    let previewLink = urlNoReturnPath;
    let extraLinkProps = {};
    // for admins and coordinators use a different preview URL
    if (isAdminOrCoordinator) {
      query = { returnPath };
      if (quizId) {
        previewLink = `${urlNoReturnPath}/preview`;
        query.quizId = quizId;
      }
    }

    extraLinkProps = {
      'aria-label': customDisplayName || displayName,
    };

    if (isEmbeddedResource) {
      previewLink = url;
      query = resourceQuery;
      extraLinkProps = {
        'aria-label': customDisplayName || embedVideoTitle,
        title: customDisplayName || embedVideoTitle,
      };
    }

    const resourceName = (
      <Fragment>
        <span
          className={cn('resource-label', { disabled: isLinkDisabled })}
          key="label"
        >
          {customDisplayName || displayName}
        </span>
        {resourceTypeName === ResourceTypes.ASSESSMENT && (
          <div className="sr-only">
            Progress Check {hasUnlockedIcon ? 'Unlocked' : 'Locked'}
          </div>
        )}
      </Fragment>
    );

    if (isLinkDisabled) {
      return (
        <div
          ref={this.linkRef}
          className={className}
          title={customDisplayName || displayName}
          {...extraLinkProps}
        >
          {resourceName}
        </div>
      );
    }

    const getLink = () => (
      <Link
        ref={this.linkRef}
        disabled={isLinkDisabled}
        role="button"
        // TODO external link
        onClick={(e) => external && externalLinkOnClick(e, url)}
        to={{
          pathname: previewLink,
          query,
          state: {
            unitNumber,
            topicQuestionsFilter,
            returnPath,
            focusedElement: this.props.resource.assessmentId,
            focusedElementVideo: `${this.props.resource.url}_${this.props.resource.videoId}`,
          },
        }}
        title={customDisplayName || displayName}
        target={external && '_blank'}
        className={className}
        params={metadata}
        {...extraLinkProps}
      >
        {resourceName}
      </Link>
    );

    const getPopover = () => {
      const popOverProps = {
        displayName,
        metadata,
        external,
        url,
        onClick: (e) => external && externalLinkOnClick(e, url),
        extraLinkProps,
        label: 'Access',
      };

      return (
        <ShareableResourcePopover {...popOverProps}>
          <Link title={customDisplayName || displayName} className={className}>
            <span
              className={cn('resource-label', { disabled: isLinkDisabled })}
              key="label"
            >
              {customDisplayName || displayName}
            </span>
          </Link>
        </ShareableResourcePopover>
      );
    };

    return timer ? (
      <div
        role="button"
        ref={this.linkRef}
        tabIndex={0}
        onClick={() =>
          this.handleClickTimedAssignment({
            urlNoReturnPath,
            router,
            timer,
            checkCBSessionFlag,
            showTimedAssignmentWarning,
            returnPath,
          })
        }
        className={className}
        isDisabled={isLinkDisabled}
      >
        {displayNameSpan}
      </div>
    ) : (isDistanceResource || isPartnerResource) &&
      isGoogleClassroomShareable ? (
      getPopover()
    ) : (
      getLink()
    );
  };

  renderResourceIcon = ({
    resourceIcon,
    resourceTypeName,
    isLinkDisabled,
    displayName,
    external,
    externalLinkOnClick,
    unitNumber,
    topicQuestionsFilter,
    metadata,
    url,
    resourceQuery,
    embedVideoTitle,
    isEmbeddedResource,
    isURLResource,
  }) => {
    const bareIcon = (
      <span className={cn('resource-icon')} key="icon">
        {resourceIcon}
      </span>
    );

    if (
      [ResourceTypes.URL, ResourceTypes.EMBEDDED_VIDEO].includes(
        resourceTypeName,
      )
    ) {
      let previewLink = '';
      let query = '';
      let extraLinkProps = {};
      const className = isURLResource
        ? 'resource-icon-link'
        : 'course-resource row-align-top';

      if (isEmbeddedResource) {
        previewLink = url;
        query = resourceQuery;
        extraLinkProps = {
          'aria-label': embedVideoTitle,
          title: embedVideoTitle,
        };
      }
      return (
        <Link
          tabIndex={-1}
          disabled={isLinkDisabled}
          onClick={(e) => external && externalLinkOnClick(e, url)}
          to={{
            pathname: previewLink,
            query,
            state: {
              unitNumber,
              topicQuestionsFilter,
            },
          }}
          title={displayName}
          aria-label={displayName}
          target={external && '_blank'}
          className={className}
          params={metadata}
          {...extraLinkProps}
        >
          {bareIcon}
        </Link>
      );
    }

    return bareIcon;
  };

  render() {
    const {
      resource,
      assignmentOverview,
      updateTimestamp,
      isStudent,
      unitNumber,
      subject,
      isPreApSubject,
      widgetTagColumn,
      isUnlocked,
      itemAction,
      skipCustomFilters,
      router,
      checkCBSessionFlag,
      isAdminOrCoordinator,
      savedClasses: sharedToClasses,
      withWatchedVideo,
      showFileSize,
      showSharedIcon,
      className,
      unitTitle,
      unitsLabel,
      showDescription,
      customDisplayName,
      showIcon,
      isSearchResult,
    } = this.props;
    let hasUnlockedIcon = false;
    let isHyperlinked = false;
    let courseResourceLink = null;
    let isMiconHidden = false;

    const { resourceAttributes } = this.state;
    if (!resourceAttributes) {
      return null;
    }

    const {
      assessmentId,
      displayName,
      fileSize,
      description,
      questions,
      __typename: resourceTypeName,
    } = resource;
    const { icon, external, returnPath, quizId, resourceQuery } =
      resourceAttributes;
    const isPartnerResource = resourceTypeName === ResourceTypes.PARTNER_LINK;
    const isDistanceResource = resourceTypeName === ResourceTypes.DISTANCE_LINK;
    const isEmbeddedResource =
      resourceTypeName === ResourceTypes.EMBEDDED_VIDEO;
    const isURLResource = resourceTypeName === ResourceTypes.URL;
    const isEmbeddedResourceAssigned =
      isEmbeddedResource && sharedToClasses.length > 0;
    const embedVideoTitle = isEmbeddedResource
      ? `${displayName}${description && `: ${description}`}`
      : '';
    const isPracticeQuestionsResource =
      resourceTypeName === ResourceTypes.PRACTICE_QUESTIONS;

    let resourceIcon;
    if (isPreApSubject || !assessmentId) {
      const iconSize = '20';

      const addIconTitle = [
        ResourceTypes.URL,
        ResourceTypes.EMBEDDED_VIDEO,
      ].includes(resourceTypeName);
      const title = (addIconTitle && (embedVideoTitle || displayName)) || '';

      if (isStudent && withWatchedVideo) {
        resourceIcon = (
          <Icon
            className="resource-icon--video-watched"
            name="watched"
            width={iconSize}
            height="14"
            title={title}
          />
        );
      } else {
        resourceIcon = (
          <Icon name={icon} width={iconSize} height={iconSize} title={title} />
        );
      }
    } else if (assessmentId) {
      if (isStudent && assignmentOverview) {
        const {
          id,
          status,
          showResults,
          scoringCompletedAt,
          link,
          assignments,
        } = assignmentOverview;
        const assignmentsWithResults = (assignments || []).filter(
          ({ showResults: shouldShow }) => shouldShow,
        );

        if (isSearchResult) {
          if (assignmentsWithResults && assignmentsWithResults.length === 1) {
            isHyperlinked = true;
            courseResourceLink = `/${assignmentsWithResults[0].link}`;
          } else {
            isHyperlinked = false;
          }
        } else if (status === 'opened') {
          hasUnlockedIcon = true;
          isHyperlinked = true;

          if (scoringCompletedAt) {
            isHyperlinked = true;

            if (!!showResults) {
              courseResourceLink = `/${link}`;
            } else {
              /**
               * TODO: This should come from the API as the rest of the links.
               * UI Workaround for 1/8 release.
               * */
              const urlPrefix = link.split('/results')[0];
              courseResourceLink = `/${urlPrefix}/review/${id}`;
              isMiconHidden = true;
            }
          }
        } else if (scoringCompletedAt && !!showResults) {
          isHyperlinked = true;
          courseResourceLink = `/${link}`;
        }
      } else {
        hasUnlockedIcon = isUnlocked;
      }

      const statusIcon = (
        <Icon
          className={cn('resource-icon-withstatus', {
            'is-disabled': !isHyperlinked,
          })}
          name={hasUnlockedIcon ? 'unlocked' : 'locked'}
          width="16"
          height="16"
        />
      );

      if (itemAction) {
        resourceIcon = React.cloneElement(itemAction, {
          buttonIcon: statusIcon,
          isDisabled: !isHyperlinked,
        });
      } else {
        resourceIcon = null;
      }
    }

    let { url, metadata } = resourceAttributes;

    const children = React.Children.map(
      this.props.children,
      (child) =>
        child &&
        React.cloneElement(child, {
          updateTimestamp,
          assignmentOverview,
          returnPath,
          url,
          external,
        }),
    );

    if (isStudent) {
      if (assessmentId) {
        if (courseResourceLink) {
          url = courseResourceLink;
        } else {
          const { link, scoringCompletedAt } = assignmentOverview || {};
          const alteredLink =
            isHyperlinked && scoringCompletedAt !== null ? link : null;

          if (!!alteredLink) {
            url = !!alteredLink ? `/${alteredLink}` : null;
          } else {
            url = !!link ? `/${link}` : null;
          }
        }
      } else {
        isHyperlinked = !!url;
      }
    }

    const urlNoReturnPath = url && url.split('?')[0];

    const topicQuestionsFilter = isPreApSubject
      ? metadata
      : getTopicQuestionsFilter({
          subjectId: parseInt(subject, 10),
          unitNumber,
          metadata,
          widgetTagColumn,
          skipCustomFilters,
          unitTitle,
          unitsLabel,
        });

    const isLinkDisabled = isStudent && !isHyperlinked ? true : !url;
    const { timer, headline } = assignmentOverview || {};
    const showTimedAssignmentWarning = headline === 'BEGIN';

    const sharedIcon = showSharedIcon && isEmbeddedResourceAssigned && (
      <span className="resource-icon" key="icon-assigned">
        <Icon className="" name="arrow_assigned" width="12" height="10" />
      </span>
    );

    const containerClassName = cn('course-resource', className, {
      'resource-with-description': isURLResource,
      partner: isPartnerResource,
      distance: isDistanceResource,
      'empty-space': !(isPreApSubject || isEmbeddedResourceAssigned),
    });

    const renderResourceProps = {
      isLinkDisabled,
      displayName,
      external,
      externalLinkOnClick: this.onClick,
      unitNumber,
      topicQuestionsFilter,
      metadata,
      url,
      resourceQuery,
      embedVideoTitle,
      isEmbeddedResource,
      isURLResource,
      customDisplayName,
    };

    const resourceIconElement =
      showIcon &&
      this.renderResourceIcon({
        resourceIcon,
        resourceTypeName,
        ...renderResourceProps,
      });
    const resourceTitleElement = this.renderResourceLink({
      urlNoReturnPath,
      returnPath,
      quizId,
      router,
      timer,
      checkCBSessionFlag,
      showTimedAssignmentWarning,
      isAdminOrCoordinator,
      hasUnlockedIcon,
      resourceTypeName,
      isDistanceResource,
      isPartnerResource,
      isURLResource,
      isGoogleClassroomShareable: resource.isGoogleClassroomShareable,
      resourceIcon,
      ...renderResourceProps,
    });
    const resourceDescriptionElement = showDescription &&
      description &&
      !isEmbeddedResource && (
        <div className="resource-description">{description}</div>
      );

    return (
      <Fragment>
        <div className={containerClassName}>
          {isURLResource && description && showDescription ? (
            <ResourceWithDescription
              icon={resourceIconElement}
              title={resourceTitleElement}
              description={resourceDescriptionElement}
            />
          ) : (
            <Fragment>
              {sharedIcon}
              {resourceIconElement}
              {resourceTitleElement}
              {resourceDescriptionElement}
            </Fragment>
          )}
          {showFileSize && fileSize && (
            <div className="resource-document-type">
              PDF Document | {fileSize}KB{' '}
              {!isStudent && (
                <Fragment>
                  <span>| </span>
                  <Icon name="educator" height="17" width="17" />
                </Fragment>
              )}
            </div>
          )}
        </div>
        {isPracticeQuestionsResource && questions && (
          <ul>
            {questions.map((question) => (
              <li key={question.index} className="topic">
                {question.index} - {question.title}
              </li>
            ))}
          </ul>
        )}
        {!isMiconHidden &&
          React.Children.map(children, (child) =>
            React.cloneElement(child, { isLinkDisabled, url }),
          )}
      </Fragment>
    );
  }
}

function mapStateToProps(state) {
  const {
    subject: {
      data: { isPreApSubject },
    },
    featureFlag,
    userData: { isTeacher, isCoordinator, isAdmin },
  } = state;
  return {
    isPreApSubject,
    checkCBSessionFlag:
      featureFlag.cb_session_expiration_check_before_take_assignment,
    cbSessionExpirationOverride: featureFlag.cb_session_expiration_override,
    isTeacher,
    isAdminOrCoordinator: isAdmin || isCoordinator,
    levityUseCreateTime: featureFlag.levity_use_createtime,
  };
}

export default withRouter(
  connectLean({
    mapState(state) {
      const {
        updateTimestamp,
        assignmentOverview,
        savedClasses,
        withWatchedVideo,
      } = state;

      return {
        updateTimestamp,
        assignmentOverview,
        savedClasses: savedClasses || [],
        withWatchedVideo,
      };
    },
  })(connect(mapStateToProps)(CourseResource)),
);

export { CourseResource };
